import * as React from 'react';
import styled from 'styled-components';

import * as icons from '@creditornot/alchemy-react/icons';
import { addInteractionEvent } from '@creditornot/browser-telemetry';
import { IconButton } from '@creditornot/cb-button';
import { color } from '@creditornot/cb-ingredients/design-tokens';
import { WoltInboundLoad as InboundLoadT } from 'common/api-types/warehouse';
import { ContentContainer } from 'common/components/cookbook/ContentContainer';
import { Typography as T } from 'common/components/cookbook/Typography/Typography';
import { useFormatMessage } from 'common/hooks/useFormatMessage';
import { useSelectedWarehouse } from 'common/hooks/useSelectedWarehouse';
import { media } from 'common/media';

import { InboundLoadItem } from './InboundLoadItem/InboundLoadItem';
import { useExpandedInboundLoads } from './useExpandedInboundLoads';

const Root = styled.div`
  &:hover {
    cursor: pointer;
  }
`;

const HeaderContainer = styled(ContentContainer)`
  display: flex;
  border-bottom: 1px solid ${color.border};
  @media ${media.sm} {
    padding-top: 24px;
    padding-bottom: 24px;
  }
`;

const LeftColumn = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
`;

const RightColumn = styled.div`
  display: flex;
  align-items: center;
  margin-left: 16px;
`;

const OrderSummaryRow = styled.div`
  margin-top: 12px;
`;

const OrderSummaryText = styled(T.body)`
  color: ${color.textSubdued};
`;

const ProgressBar = styled.div<{ $progressPercentage: number }>`
  height: 4px;
  background: ${color.bgSurface};
  border-radius: 2px;
  margin-top: 12px;
  &::after {
    content: '';
    display: block;
    height: 4px;
    background: ${color.bgFillPositive};
    border-radius: 2px;
    width: ${({ $progressPercentage }) => $progressPercentage}%;
  }
`;

const getProgressPercentage = (orderedUnits: number, receivedUnits: number) => {
  const progress = (receivedUnits / orderedUnits) * 100;

  return progress > 100 ? 100 : progress;
};

type Props = {
  inboundLoad: InboundLoadT;
  poIndex: number;
  onItemClick: (itemId: string, inboundLoadId: string) => void;
};

export const InboundLoad = ({ inboundLoad, poIndex, onItemClick }: Props) => {
  const formatMessage = useFormatMessage();
  const selectedWarehouseId = useSelectedWarehouse().id as string;

  const {
    id: inboundLoadId,
    publicId,
    items,
    vendor,
    totalOrderedQuantity,
    totalReceivedQuantity,
    orderRowCount,
  } = inboundLoad;

  const totalProgress = React.useMemo(
    () => getProgressPercentage(totalOrderedQuantity.value, totalReceivedQuantity.value),
    [totalOrderedQuantity.value, totalReceivedQuantity.value],
  );

  const [expandedInboundLoads, setExpandedInboundLoads] = useExpandedInboundLoads();

  const handleInboundLoadClick = React.useCallback(() => {
    setExpandedInboundLoads((loads) => {
      return loads.includes(inboundLoadId)
        ? loads.filter((id) => id !== inboundLoadId)
        : [...loads, inboundLoadId];
    });
  }, [inboundLoadId, setExpandedInboundLoads]);

  const isExpanded = React.useMemo(
    () => expandedInboundLoads.includes(inboundLoadId),
    [expandedInboundLoads, inboundLoadId],
  );

  return (
    <Root
      onClick={() => {
        handleInboundLoadClick();
      }}
    >
      <HeaderContainer verticalPadding="sm">
        <LeftColumn>
          <div>
            <T.bodyLabel>{publicId}</T.bodyLabel>
          </div>
          <ProgressBar $progressPercentage={totalProgress} />
          <OrderSummaryRow>
            <OrderSummaryText>
              {vendor.name}
              {' · '}
              {orderRowCount}{' '}
              {formatMessage('receiving.item_plural', {
                itemCount: orderRowCount,
              })}
              {' · '}
              {totalOrderedQuantity.value}{' '}
              {formatMessage('common.unit_plural', {
                unitCount: totalOrderedQuantity.value,
              })}
            </OrderSummaryText>
          </OrderSummaryRow>
        </LeftColumn>
        <RightColumn>
          {isExpanded ? (
            <IconButton size="small" variant="neutral" icon={icons.chevronUp} />
          ) : (
            <IconButton size="small" variant="neutral" icon={icons.chevronDown} />
          )}
        </RightColumn>
      </HeaderContainer>
      {isExpanded &&
        (items || []).map((item, index) => (
          <InboundLoadItem
            key={item.itemId}
            inboundLoadPublicId={publicId}
            inboundLoadItem={item}
            onClick={(itemId, inboundLoadId) => {
              onItemClick(itemId, inboundLoadId);
              addInteractionEvent('receiving', 'click', {
                wm_venue_id: selectedWarehouseId,
                item_index: index,
                item_id: itemId,
                source: 'purchase_orders',
                purchase_order_public_id: publicId,
                purchase_order_index: poIndex,
              });
            }}
            isLastElement={index === items.length - 1}
          />
        ))}
    </Root>
  );
};
